/**
 * Runs `worker` over `items` with at most `limit` in flight.
 * NEVER rejects: a worker rejection resolves that slot to `null`.
 * `onSettled` fires as each item settles, enabling progressive UI commits.
 * Order of the returned array matches `items`; settle order does not.
 * An empty `items` array resolves `[]`. `limit <= 0` with non-empty `items`
 * runs no workers and resolves an `items.length` array filled with `null`
 * (shape stays index-aligned with `items`).
 *
 * @param {Array<T>} items
 * @param {number} limit
 * @param {(item: T, index: number) => Promise<R>} worker
 * @param {(item: T, index: number, value: R|null) => void} [onSettled]
 * @returns {Promise<Array<R|null>>}
 */
export async function mapWithConcurrency(items, limit, worker, onSettled) {
  if (!items || items.length === 0) return [];

  const results = new Array(items.length).fill(null);
  let cursor = 0;

  const runner = async () => {
    while (cursor < items.length) {
      const i = cursor++; // sync increment, no await before it
      let value = null;
      try {
        value = await worker(items[i], i);
      } catch {
        value = null;
      }
      results[i] = value;
      if (onSettled) {
        try {
          onSettled(items[i], i, value);
        } catch {
          // onSettled must never abort the batch — mapWithConcurrency never rejects.
        }
      }
    }
  };

  const poolSize = Math.max(0, Math.min(limit, items.length));
  await Promise.all(Array.from({ length: poolSize }, runner));

  return results;
}
